import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";
import SizeSetQtyModal from "./SizeSetQtyModal";
import { mergeSetSizes } from "./sizeSetEntry";

export default function SProductQty({
  setFormData,
  updateFunc,
  formData,
  isProductUpdate,
}) {
  /*   All States
   ********************************************* */
  const [allSizes, setAllSizes] = useState([]);
  const [allPrice, setAllPrice] = useState(0);

  const [isUpdateMode, setIsUpdateMode] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [updatePriceModal, setUpdatePriceModal] = useState(false);
  const [updateData, setUpdateData] = useState(null);

  const [selectedIndex, setSelectedIndex] = useState(null);

  // Size Sets (Products -> Sizes -> Size Sets, user 2026-10-27): picking one
  // opens its sizes with Qty / Price boxes; only the sizes given a quantity
  // go on the product (user, 2026-10-30).
  const [sizeSets, setSizeSets] = useState([]);
  const [showSetModal, setShowSetModal] = useState(false);
  const [qtySet, setQtySet] = useState(null);
  useEffect(() => {
    API.post("/sales/size-sets", {}, { headers: authHeader() })
      .then(({ data }) => setSizeSets(data?.data || []))
      .catch(() => {});
  }, []);

  const [sizeData, setSizeData] = useState({
    size: "",
    quantity: 0,
    price: 0,
    cost: 0,
  });

  /*   All Functions
   ********************************************* */
  // helpers to compute totals safely
  const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const toInt = (v) => {
    const n = parseInt(v);
    return Number.isFinite(n) ? n : 0;
  };
  const computeTotals = (sizes) => {
    return sizes?.reduce(
      (acc, s) => {
        const qty = toInt(s?.quantity);
        const price = toNum(s?.price);
        acc.totalQuantity += qty;
        acc.totalPrice += qty * price;
        return acc;
      },
      { totalQuantity: 0, totalPrice: 0 },
    );
  };

  // Persist current in-modal edits into component state (but not DB)
  const saveCurrentEdit = () => {
    const current = sortedSizes[selectedIndex] || updateData;
    if (!current) return;
    const targetId = current.id;
    const targetSortId = current.sortId;

    const newArray = allSizes?.map((item) =>
      item.id === targetId
        ? {
            ...sizeData,
            id: item.id,
            sortId: targetSortId,
            cost: toNum(sizeData?.price) * toInt(sizeData?.quantity),
          }
        : item,
    );

    setAllSizes(newArray);
    const totals = computeTotals(newArray);
    setFormData((prev) => ({
      ...prev,
      productSizes: JSON.stringify(newArray),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    }));

    const updatedItem = newArray.find((it) => it.id === targetId);
    if (updatedItem) setUpdateData(updatedItem);
  };

  const resetSizeForm = () => {
    setUpdateData(null);
    setIsUpdateMode(false);
    setSelectedIndex(null);
    setSizeData({ size: "", quantity: 0, price: 0, cost: 0 });
  };

  const handleAddSize = (e) => {
    e.preventDefault();

    const newItem = {
      ...sizeData,
      cost: toNum(sizeData?.price) * toInt(sizeData?.quantity),
      id: uuid(),
      sortId: allSizes.length ? allSizes[allSizes.length - 1].sortId + 1 : 1,
    };
    const data = [...allSizes, newItem];

    setAllSizes(data);
    const totals = computeTotals(data);
    // Persist as JSON string to match formData shape in DB and update totals
    setFormData((prev) => ({
      ...prev,
      productSizes: JSON.stringify(data),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    }));

    setShowAddModal(false);
    resetSizeForm();
  };

  // The size set modal's quantities: a size already on the product takes the
  // typed quantity and price, any other is added after them.
  const applySetEntries = (entries) => {
    const { sizes, totalQuantity, totalPrice } = mergeSetSizes(allSizes, entries);
    setAllSizes(sizes);
    setFormData((prev) => ({
      ...prev,
      productSizes: JSON.stringify(sizes),
      totalQuantity,
      totalPrice,
    }));
    setQtySet(null);
  };

  const handleUpdateSize = async (e) => {
    e.preventDefault();
    const current = sortedSizes[selectedIndex] || updateData;
    if (!current) return;
    const targetId = current.id;
    const targetSortId = current.sortId;

    const newArray = allSizes?.map((item) =>
      item.id === targetId
        ? {
            ...sizeData,
            id: item.id,
            sortId: targetSortId,
            cost: toNum(sizeData?.price) * toInt(sizeData?.quantity),
          }
        : item,
    );

    setAllSizes(newArray);
    const totals = computeTotals(newArray);
    setFormData((prev) => ({
      ...prev,
      productSizes: JSON.stringify(newArray),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    }));
    resetSizeForm();
    setShowAddModal(false);
  };

  const handleUpdateSizeAndDb = async (e) => {
    e.preventDefault();
    if (!isProductUpdate) return; // prevent DB update when adding a new product
    const current = sortedSizes[selectedIndex] || updateData;
    if (!current) return;
    const targetId = current.id;
    const targetSortId = current.sortId;

    const newArray = allSizes?.map((item) =>
      item.id === targetId
        ? {
            ...sizeData,
            id: item.id,
            sortId: targetSortId,
            cost: toNum(sizeData?.price) * toInt(sizeData?.quantity),
          }
        : item,
    );

    setAllSizes(newArray);
    const totals = computeTotals(newArray);
    const updatedForm = {
      ...formData,
      productSizes: JSON.stringify(newArray),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    };
    setFormData(updatedForm);
    await updateFunc(e, updatedForm);
  };

  const handleUpdateAllSize = async (e) => {
    e.preventDefault();
    const updatedArray = allSizes.map((cur) => ({
      ...cur,
      price: allPrice,
      cost: toNum(allPrice) * toInt(cur?.quantity),
    }));
    setAllSizes(updatedArray);

    const totals = computeTotals(updatedArray);
    const updatedForm = {
      ...formData,
      productSizes: JSON.stringify(updatedArray),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    };
    setFormData(updatedForm);
    setAllPrice(0);
    if (isProductUpdate) {
      await updateFunc(e, updatedForm);
    }
    setUpdatePriceModal(false);
  };

  // Inline cell editing — updates allSizes + formData on every keystroke
  const handleInlineChange = (id, field, value) => {
    const newData = allSizes.map((item) =>
      item.id === id
        ? {
            ...item,
            [field]: value,
            cost:
              field === "quantity"
                ? toNum(item.price) * toInt(value)
                : toNum(value) * toInt(item.quantity),
          }
        : item,
    );
    setAllSizes(newData);
    const totals = computeTotals(newData);
    setFormData((prev) => ({
      ...prev,
      productSizes: JSON.stringify(newData),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    }));
  };

  // Enter key — persist to DB only when in update mode
  const handleInlineCommit = async () => {
    if (!isProductUpdate) return;
    const totals = computeTotals(allSizes);
    const updatedForm = {
      ...formData,
      productSizes: JSON.stringify(allSizes),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    };
    await updateFunc(null, updatedForm);
  };

  const deleteSize = (id) => {
    setAllSizes((prev) => {
      const filteredArray = prev.filter((pre) => pre.id !== id);
      const sortedArr = filteredArray.map((item, i) => ({
        ...item,
        sortId: i + 1,
      }));
      const totals = computeTotals(sortedArr);
      setFormData((formPrev) => ({
        ...formPrev,
        productSizes: JSON.stringify(sortedArr),
        totalQuantity: totals.totalQuantity,
        totalPrice: totals.totalPrice,
      }));
      return sortedArr;
    });
  };

  /*   All UseEffects
   ********************************************* */

  // Use a stable, memoized, sorted view for consistent indexing
  const sortedSizes = useMemo(() => {
    const source = Array.isArray(allSizes) ? allSizes : [];
    return [...source].sort((a, b) => a.sortId - b.sortId);
  }, [allSizes]);

  const sortUp = (index) => {
    const sorted = [...sortedSizes];
    const above = sorted[index - 1];
    const curr = sorted[index];
    if (!above || !curr) return;

    const newData = allSizes.map((item) => {
      if (item.id === above.id) return { ...item, sortId: curr.sortId };
      if (item.id === curr.id) return { ...item, sortId: above.sortId };
      return item;
    });

    setAllSizes(newData);
    const totals = computeTotals(newData);
    setFormData((prev) => ({
      ...prev,
      productSizes: JSON.stringify(newData),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    }));
  };

  const sortDown = (index) => {
    const sorted = [...sortedSizes];
    const curr = sorted[index];
    const below = sorted[index + 1];
    if (!below || !curr) return;

    const newData = allSizes.map((item) => {
      if (item.id === below.id) return { ...item, sortId: curr.sortId };
      if (item.id === curr.id) return { ...item, sortId: below.sortId };
      return item;
    });

    setAllSizes(newData);
    const totals = computeTotals(newData);
    setFormData((prev) => ({
      ...prev,
      productSizes: JSON.stringify(newData),
      totalQuantity: totals.totalQuantity,
      totalPrice: totals.totalPrice,
    }));
  };

  useEffect(() => {
    if (selectedIndex === null || selectedIndex === undefined) return;
    const selectedSize = sortedSizes[selectedIndex];
    setSizeData({
      size: selectedSize?.size?.toUpperCase(),
      quantity: selectedSize?.quantity,
      price: selectedSize?.price,
    });
    // Keep updateData aligned with the currently selected item
    if (selectedSize) setUpdateData(selectedSize);
  }, [sortedSizes, selectedIndex]);

  useEffect(() => {
    setAllSizes((prev) =>
      formData?.productSizes
        ? typeof formData?.productSizes === "string"
          ? JSON.parse(formData?.productSizes)
          : formData?.productSizes
        : [],
    );
  }, [formData?.productSizes]);

  return (
    <div className="flex-grow-1 d-flex flex-column">
      <div
        className="bg-white custom-shadow-sm d-flex flex-column flex-grow-1 mb-2 p-1 position-relative"
        style={{ borderRadius: 15, height: 350 }}
      >
        {/* Floating "Add Size Set" (user, 2026-10-27): bottom right of the
            sizes card, opens the set picker. */}
        {sizeSets.length > 0 && (
          <button
            type="button"
            className="btn btn-primary rounded-circle shadow position-absolute d-flex align-items-center justify-content-center"
            style={{ right: 22, bottom: 14, width: 44, height: 44, zIndex: 2 }}
            title="Add Size Set"
            aria-label="Add Size Set"
            onClick={() => setShowSetModal(true)}
          >
            <i className="mdi mdi-format-list-bulleted-square fs-4"></i>
          </button>
        )}
        <div className="" style={{ overflowY: "scroll", height: "98%" }}>
          <div className="table-responsive  ">
            <table className="table ">
              <thead>
                <tr>
                  <th scope="col" className="text-end">
                    Size
                  </th>
                  <th
                    scope="col"
                    className="text-center"
                    style={{ width: "100px" }}
                  >
                    Quantity
                  </th>
                  <th
                    scope="col"
                    className="text-center"
                    style={{ width: "100px" }}
                  >
                    Price
                  </th>
                  <th scope="col" className="text-end">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedSizes?.map((item, i) => (
                  <tr key={i} className="">
                    <td className="py-0 align-middle text-end">
                      {item?.size?.toUpperCase()}
                    </td>
                    <td className="py-0 text-center">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={item?.quantity}
                        tabIndex={i + 1}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          handleInlineChange(
                            item.id,
                            "quantity",
                            e.target.value,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleInlineCommit();
                        }}
                        className="form-control hide-input-arrow text-center form-control-sm border-bottom border-1 border-dark rounded-0"
                        style={{ width: "80px" }}
                      />
                    </td>
                    <td className="py-0 text-center">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={item?.price}
                        tabIndex={sortedSizes.length + i + 1}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          handleInlineChange(item.id, "price", e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleInlineCommit();
                        }}
                        className="form-control hide-input-arrow text-center form-control-sm border-bottom border-1 border-dark rounded-0"
                        style={{ width: "80px" }}
                      />
                    </td>
                    <td className="py-0 text-end">
                      <>
                        <div className="action-icon">
                          <div
                            onClick={() => {
                              item.sortId !== 1 && sortUp(i);
                            }}
                            className="action-icon"
                          >
                            <i className="mdi mdi-chevron-double-up"></i>
                          </div>
                          <div
                            onClick={() => {
                              item.sortId !== allSizes?.length && sortDown(i);
                            }}
                            className="action-icon"
                          >
                            <i className="mdi mdi-chevron-double-down"></i>
                          </div>
                          <i
                            onClick={() => {
                              setUpdateData(item);
                              setIsUpdateMode(true);
                              setSelectedIndex(i);
                              setShowAddModal(true);
                            }}
                            className="mdi mdi-pencil"
                          ></i>
                        </div>
                        <div
                          onClick={() => deleteSize(item.id)}
                          className="action-icon"
                        >
                          <i className="mdi mdi-delete"></i>
                        </div>
                      </>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="d-flex">
        <div
          className="d-flex justify-content-evenly flex-grow-1 py-1 align-items-center bg-white shadow-lg me-2"
          style={{ borderRadius: 15 }}
        >
          <div>
            Total Qty
            <span className="border ms-1 px-2">
              {parseFloat(formData?.totalQuantity)}
            </span>
          </div>
          <div>
            Total Price
            <span className="border ms-1 px-2">
              $ {parseFloat(formData?.totalPrice).toFixed(2)}
            </span>
          </div>
        </div>
        <>
          <div className="d-flex">
            <button
              onClick={(e) => {
                setUpdatePriceModal(true);
              }}
              className="btn btn-primary me-1"
              style={{ borderRadius: 10 }}
              type="button"
            >
              Update Price
            </button>
          </div>
          <div className="d-flex">
            <button
              onClick={() => {
                resetSizeForm();
                setShowAddModal(true);
              }}
              className="btn btn-primary"
              style={{ borderRadius: 10 }}
              type="button"
            >
              Add Sizes
            </button>
          </div>
        </>
      </div>
      <SizeSetQtyModal
        show={!!qtySet}
        sets={qtySet ? [qtySet] : []}
        onApply={applySetEntries}
        onHide={() => setQtySet(null)}
      />
      {/*   Add Size Set Modal
       ********************************************* */}
      <Modal
        show={showSetModal}
        onHide={() => setShowSetModal(false)}
        centered
        contentClassName="border shadow-lg"
      >
        <Modal.Body className="bg-white" style={{ borderRadius: 15 }}>
          <h4 className="mb-1">Add Size Set</h4>
          <div className="text-muted small mb-3">
            Pick a set, then enter the quantities. Only the sizes given a
            quantity are added.
          </div>
          <div className="list-group">
            {sizeSets.map((set) => (
              <button
                key={set.id}
                type="button"
                className="list-group-item list-group-item-action"
                disabled={!set.items?.length}
                onClick={() => {
                  setShowSetModal(false);
                  setQtySet(set);
                }}
              >
                <div className="fw-bold">
                  {set.name}{" "}
                  <span className="text-muted small fw-normal">
                    ({set.items?.length || 0})
                  </span>
                </div>
                <div className="small text-muted">
                  {(set.items || []).map((i) => i.size?.name).filter(Boolean).join(", ") ||
                    "No sizes"}
                </div>
              </button>
            ))}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSetModal(false)}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
      {/*   ADD Size Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body className="bg-white shadow-lg" style={{ borderRadius: 15 }}>
          <div className="mb-13 d-flex justify-content-between mb-2">
            <h4>{isUpdateMode ? "UPDATE" : "ADD"} SIZES</h4>
            {isUpdateMode && (
              <div>
                <button
                  onClick={() => {
                    // move to previous in the sorted view
                    if (selectedIndex === 0) return;
                    // save any edits to the current size before moving
                    saveCurrentEdit();
                    const newIndex = (selectedIndex ?? 0) - 1;
                    setSelectedIndex(newIndex);
                    if (sortedSizes[newIndex])
                      setUpdateData(sortedSizes[newIndex]);
                  }}
                  type="button"
                  className={"btn btn-success btn-sm p-0 px-1 mx-1"}
                >
                  <i className="mdi mdi-arrow-left-bold fs-3 py-0 my-0"></i>
                </button>
                <button
                  onClick={() => {
                    // move to next in the sorted view
                    if (selectedIndex === sortedSizes?.length - 1) return;
                    // save any edits to the current size before moving
                    saveCurrentEdit();
                    const newIndex = (selectedIndex ?? 0) + 1;
                    setSelectedIndex(newIndex);
                    if (sortedSizes[newIndex])
                      setUpdateData(sortedSizes[newIndex]);
                  }}
                  type="button"
                  className={"btn btn-success btn-sm p-0 px-1"}
                >
                  <i className="mdi mdi-arrow-right-bold fs-3 py-0 my-0"></i>
                </button>
              </div>
            )}
          </div>
          <hr className="mt-0" />
          <form onSubmit={(e) => e.preventDefault()}>
            {/* sizeData */}
            {sizeData ? (
              <>
                <div className="row mb-1">
                  <label className="col-2 my-auto">Size</label>
                  <div className="col-10 ">
                    <input
                      type="text"
                      value={sizeData?.size}
                      autoFocus
                      required
                      onChange={(e) => {
                        setSizeData({
                          ...sizeData,
                          size: e.target.value?.toUpperCase(),
                        });
                      }}
                      className="form-control"
                      placeholder="Enter Size"
                    />
                  </div>
                </div>
                <div className="row mb-1">
                  <label className="col-2 my-auto">Quantity</label>
                  <div className="col-10 ">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      required
                      value={sizeData?.quantity}
                      onChange={(e) => {
                        setSizeData({
                          ...sizeData,
                          quantity: e.target.value,
                          cost:
                            parseInt(sizeData?.price) *
                            parseInt(e.target.value),
                        });
                      }}
                      className="form-control"
                      placeholder="Enter Quantity"
                    />
                  </div>
                </div>
                <div className="row mb-1">
                  <label className="col-2 my-auto">Price</label>
                  <div className="col-10 ">
                    <input
                      type="text"
                      required
                      value={sizeData?.price}
                      onChange={(e) => {
                        setSizeData({
                          ...sizeData,
                          price: e.target.value,
                          cost:
                            parseFloat(e.target.value) *
                            parseFloat(sizeData?.quantity),
                        });
                      }}
                      className="form-control"
                      placeholder="Enter Price"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="row mb-1">
                  <label className="col-2 my-auto">Size</label>
                  <div className="col-10 ">
                    <input
                      type="text"
                      value={sizeData?.size}
                      required
                      onChange={(e) => {
                        setSizeData({ ...sizeData, size: e.target.value });
                      }}
                      className="form-control"
                      placeholder="Enter Size"
                    />
                  </div>
                </div>
                <div className="row mb-1">
                  <label className="col-2 my-auto">Quantity</label>
                  <div className="col-10 ">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      required
                      value={sizeData?.quantity}
                      onChange={(e) => {
                        setSizeData({
                          ...sizeData,
                          quantity: e.target.value,
                          cost:
                            parseInt(sizeData?.price) *
                            parseInt(e.target.value),
                        });
                      }}
                      className="form-control"
                      placeholder="Enter Quantity"
                    />
                  </div>
                </div>
                <div className="row mb-1">
                  <label className="col-2 my-auto">Price</label>
                  <div className="col-10 ">
                    <input
                      type="text"
                      required
                      value={sizeData?.price}
                      onChange={(e) => {
                        setSizeData({
                          ...sizeData,
                          price: e.target.value,
                          cost:
                            parseFloat(e.target.value) *
                            parseFloat(sizeData?.quantity),
                        });
                      }}
                      className="form-control"
                      placeholder="Enter Price"
                    />
                  </div>
                </div>
              </>
            )}

            {/*   buttons
             ********************************************* */}
            <div className="my-2">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetSizeForm();

                    resetSizeForm();
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                {!updateData && (
                  <button
                    className="btn btn-primary btn-sm ms-1"
                    onClick={handleAddSize}
                    type="submit"
                  >
                    ADD
                  </button>
                )}
                {updateData && (
                  <>
                    <button
                      className="btn btn-info btn-sm ms-1"
                      onClick={handleUpdateSize}
                      type="submit"
                    >
                      UPDATE & Close
                    </button>
                    {isProductUpdate && (
                      <button
                        className="btn btn-primary btn-sm ms-1"
                        onClick={handleUpdateSizeAndDb}
                        type="button"
                      >
                        UPDATE DB
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      {/*   Update Size Modal
       ********************************************* */}
      <Modal show={updatePriceModal} onHide={() => setUpdatePriceModal(false)}>
        <Modal.Body className="bg-white shadow-lg" style={{ borderRadius: 15 }}>
          <div className="mb-13 d-flex justify-content-between mb-2">
            <h4>UPDATE PRICE FOR ALL SIZES</h4>
          </div>
          <hr className="mt-0" />
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="row mb-1">
              <label className="col-2 my-auto">Price</label>
              <div className="col-10 ">
                <input
                  type="text"
                  value={allPrice}
                  required
                  autoFocus
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onChange={(e) => {
                    setAllPrice(e.target.value);
                  }}
                  className="form-control"
                  placeholder="Enter Price"
                />
              </div>
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="my-2">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setUpdatePriceModal(false);
                    resetSizeForm();
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>

                <button
                  className="btn btn-primary btn-sm ms-1"
                  type="button"
                  onClick={handleUpdateAllSize}
                >
                  UPDATE
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
